/**
 * Location Document API Contract
 *
 * Endpoints for attaching/detaching files to locations.
 * Files are company-level assets linked via LocationDocument junction table.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { AttachLocationDocumentInputSchema, UpdateLocationDocumentInputSchema, LocationIdParamsSchema, LocationDocumentParamsSchema, LocationDocumentResponseSchema, LocationDocumentListResponseSchema, } from "@hourly/api/locationDocument";
// ============================================
// Contracts
// ============================================
/**
 * GET /locations/:locationId/documents
 * List all documents attached to a location
 * Requires: location:view permission
 */
export const listDocuments = oc
    .route({
    method: "GET",
    path: "/locations/{locationId}/documents",
    summary: "List documents attached to a location",
    tags: ["Location Documents"],
})
    .input(LocationIdParamsSchema)
    .output(LocationDocumentListResponseSchema);
/**
 * POST /locations/:locationId/documents
 * Attach a file to a location
 * Requires: location:update permission
 * Returns 201 Created on success
 */
export const attachDocument = oc
    .route({
    method: "POST",
    path: "/locations/{locationId}/documents",
    summary: "Attach a file to a location",
    tags: ["Location Documents"],
    successStatus: 201,
    inputStructure: "detailed",
})
    .input(z.object({
    params: LocationIdParamsSchema,
    body: AttachLocationDocumentInputSchema,
}))
    .output(LocationDocumentResponseSchema);
/**
 * GET /locations/:locationId/documents/:documentId
 * Get a single document by ID
 * Requires: location:view permission
 */
export const getDocument = oc
    .route({
    method: "GET",
    path: "/locations/{locationId}/documents/{documentId}",
    summary: "Get a document by ID",
    tags: ["Location Documents"],
})
    .input(LocationDocumentParamsSchema)
    .output(LocationDocumentResponseSchema);
/**
 * PATCH /locations/:locationId/documents/:documentId
 * Update document metadata (expiration date, notes)
 * Requires: location:update permission
 */
export const updateDocument = oc
    .route({
    method: "PATCH",
    path: "/locations/{locationId}/documents/{documentId}",
    summary: "Update document metadata",
    tags: ["Location Documents"],
    inputStructure: "detailed",
})
    .input(z.object({
    params: LocationDocumentParamsSchema,
    body: UpdateLocationDocumentInputSchema,
}))
    .output(LocationDocumentResponseSchema);
/**
 * DELETE /locations/:locationId/documents/:documentId
 * Detach a file from a location (does NOT delete the file itself)
 * Requires: location:update permission
 * Returns 204 No Content on success
 */
export const detachDocument = oc
    .route({
    method: "DELETE",
    path: "/locations/{locationId}/documents/{documentId}",
    summary: "Detach a file from a location",
    tags: ["Location Documents"],
    successStatus: 204,
})
    .input(LocationDocumentParamsSchema)
    .output(z.void());
/**
 * GET /my/location-documents
 * List all location documents visible to the current user across all locations
 * Requires: authenticated user (no additional permission needed beyond auth)
 */
export const listMyDocuments = oc
    .route({
    method: "GET",
    path: "/my/location-documents",
    summary: "List location documents visible to the current user",
    tags: ["Location Documents"],
})
    .input(z.object({}))
    .output(LocationDocumentListResponseSchema);
// ============================================
// Contract Router
// ============================================
export const locationDocumentContract = {
    list: listDocuments,
    attach: attachDocument,
    get: getDocument,
    update: updateDocument,
    detach: detachDocument,
    listMy: listMyDocuments,
};
//# sourceMappingURL=locationDocument.js.map